import { fmt, todayISO } from '../utils/formatters';
import { donationSourceLabel } from '../utils/constants';

/* The donation confirmation email (client spec 23-donation-confirmation-email).
   The template is the client's own HTML, kept as a plain file at
   public/emails/donation-confirmation.html so it can be edited without touching
   code; it's fetched once and cached. The Log Donation form's Preview and the
   send after Save both go through buildDonationEmail, so what the room parent
   previews is what the parent gets. */

const PUBLIC = process.env.PUBLIC_URL || '';
const TEMPLATE_URL = `${PUBLIC}/emails/donation-confirmation.html`;

/* The logo. A sent email carries it inside the message as an inline image
   (cid:), so it shows in Gmail with no public URL — a link to localhost or to a
   hashed build file breaks, and Gmail won't show SVG or data: URIs at all. The
   Preview renders in this browser, so it just uses the file. Set
   REACT_APP_EMAIL_LOGO_URL to use a hosted PNG for both instead. */
const LOGO_PATH = `${PUBLIC}/emails/assets/room-parent-mark@2x.png`;
const LOGO_CID = 'roomparent-logo';
const hostedLogoUrl = process.env.REACT_APP_EMAIL_LOGO_URL || '';
const previewLogoUrl = () => hostedLogoUrl || `${window.location.origin}${LOGO_PATH}`;

let logoPromise = null;
function loadLogoBase64() {
  if (!logoPromise) {
    logoPromise = fetch(LOGO_PATH)
      .then((r) => {
        if (!r.ok) throw new Error(`Couldn't load the email logo (${r.status}).`);
        return r.arrayBuffer();
      })
      .then((buf) => {
        let binary = '';
        new Uint8Array(buf).forEach((b) => { binary += String.fromCharCode(b); });
        return btoa(binary);
      })
      .catch((err) => {
        logoPromise = null;
        throw err;
      });
  }
  return logoPromise;
}

let templatePromise = null;
function loadTemplate() {
  if (!templatePromise) {
    templatePromise = fetch(TEMPLATE_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`Couldn't load the email template (${r.status}).`);
        return r.text();
      })
      .catch((err) => {
        templatePromise = null; // let the next attempt retry
        throw err;
      });
  }
  return templatePromise;
}

/* The template opens with a comment documenting its tokens — useful in the
   file, not in every parent's inbox. */
const stripDocComment = (tpl) => tpl.replace(/^(<!doctype html>\s*)<!--[\s\S]*?-->\s*/i, '$1');

const esc = (v) =>
  String(v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/* Mustache, the two forms the template uses: {{x}} is HTML-escaped, and
   {{#x}}…{{/x}} renders only when x is non-empty. */
export function renderMustache(tpl, data) {
  return tpl
    .replace(/\{\{#(\w+)\}\}([\s\S]*?)\{\{\/\1\}\}/g, (_, k, inner) => (data[k] ? inner : ''))
    .replace(/\{\{(\w+)\}\}/g, (_, k) => (data[k] == null ? '' : esc(data[k])));
}

/* Every token the template reads, for one donation as the form holds it.
   donation: { amount, source (API enum), dateReceived (yyyy-mm-dd), reason (label), note } */
export function donationEmailData({ donation, parentName, className, schoolName, roomParentName }) {
  /* "Other" is a bucket, not a cause — "for Other" says nothing, so it reads
     as "the class fund" */
  const isOther = !donation.reason || String(donation.reason).trim().toLowerCase() === 'other';
  const reasonLabel = isOther ? 'the class fund' : donation.reason;

  const day = /^\d{4}-\d{2}-\d{2}$/.test(donation.dateReceived || '') ? donation.dateReceived : todayISO();
  const dateReceived = new Date(`${day}T00:00:00`).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const amount = fmt(donation.amount);
  return {
    subject: `Thank you for your ${amount} donation`,
    preheader: `${className} · ${reasonLabel}`,
    parentFirstName: String(parentName || '').trim().split(/\s+/)[0] || 'friend',
    amount,
    reasonLabel,
    sourceLabel: donationSourceLabel(donation.source),
    dateReceived,
    note: donation.note || '',
    className,
    schoolName: schoolName || '',
    /* who logged it — the body promises replies reach them */
    roomParentName: roomParentName || 'your room parent',
    logoUrl: previewLogoUrl(),
  };
}

/* → { subject, html, inlineImages } for the preview iframe, or with
   { forSend: true } for sendGmailMessage — the same email, except a sent one
   carries its logo inline (see LOGO_CID) rather than by URL. */
export async function buildDonationEmail(args, { forSend = false } = {}) {
  const data = donationEmailData(args);
  const tpl = await loadTemplate();
  const inlineLogo = forSend && !hostedLogoUrl;
  const inlineImages = inlineLogo
    ? [{ cid: LOGO_CID, contentType: 'image/png', filename: 'roomparent.png', base64: await loadLogoBase64() }]
    : [];
  const html = renderMustache(stripDocComment(tpl), inlineLogo ? { ...data, logoUrl: `cid:${LOGO_CID}` } : data);
  return { subject: data.subject, html, inlineImages };
}
